import { WSList, WSPanel, WSText } from "@wingspanhq/fe-component-library";
import { Batch, BatchStatus } from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";

import { StepItem, Steps } from "../../../../shared/components/Steps";
import { selectorIsImportBatchFullyImported } from "../../selectors/selectorIsImportBatchFullyImported";
import { selectorIsImportBatchPartiallyImported } from "../../selectors/selectorIsImportBatchPartiallyImported";
import { selectorIsImportBatchUploadFinished } from "../../selectors/selectorIsImportBatchUploadFinished";
import { selectorIsImportBatchUploadInProgress } from "../../selectors/selectorIsImportBatchUploadInProgress";

type Props = {
  batch: Batch;
  isEmptyBatch: boolean;
  /** `BatchSummary.progress.failed`. */
  failedCount?: number;
};

export const ImportBatchSteps: React.FC<Props> = ({
  batch,
  isEmptyBatch,
  failedCount
}) => {
  const uploadFileStepStatus = useMemo<StepItem["status"]>(() => {
    if (selectorIsImportBatchUploadFinished({ batch, isEmptyBatch })) {
      return "Complete";
    }
    if (selectorIsImportBatchUploadInProgress(batch)) {
      return "InProgress";
    }
    return "Active";
  }, [batch, isEmptyBatch]);

  const completeImportStepStatus = useMemo<StepItem["status"]>(() => {
    if (selectorIsImportBatchFullyImported({ batch, failedCount })) {
      return "Complete";
    }
    if (selectorIsImportBatchPartiallyImported({ batch, failedCount })) {
      return "Warning";
    }
    if (batch.status === BatchStatus.FAILED) {
      return "Failed";
    }
    if (
      batch.status === BatchStatus.PENDING ||
      batch.status === BatchStatus.PROCESSING
    ) {
      return "InProgress";
    }
    if (selectorIsImportBatchUploadFinished({ batch, isEmptyBatch })) {
      return "Active";
    }
    return "Pending";
  }, [batch, isEmptyBatch, failedCount]);

  return (
    <WSPanel>
      <WSList gap="L">
        <WSText.Paragraph weight="medium" color="gray600">
          Batch import steps
        </WSText.Paragraph>
        <Steps
          steps={[
            { title: "Create batch", status: "Complete" },
            { title: "Upload spreadsheet", status: uploadFileStepStatus },
            { title: "Complete import", status: completeImportStepStatus }
          ]}
        />
      </WSList>
    </WSPanel>
  );
};
